import { useEffect, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { CheckCircle2, Loader2, XCircle, Hourglass } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { AuthImage } from './AuthImage';
import { errText } from './errText';

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3 text-xs"><span className="text-muted-foreground">{label}</span><span className="text-right font-medium">{value || '—'}</span></div>
);

export const PharmacistReview = ({ request, canApprove, onDecided }) => {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const decide = async (decision) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/controlled/requests/${request.request_id}/decision`, { decision, reason });
      onDecided({ ...request, ...data, decision_reason: reason });
    } catch (e) { toast.error(errText(e, 'Could not save the decision')); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="ctrl-pharmacist-review">
      <div className="rounded-md border p-3 space-y-1">
        <Row label="Customer" value={request.full_name} />
        <Row label="Phone" value={request.phone} />
        <Row label="Address" value={request.address} />
        <Row label="Ghana Card" value={`${request.ghana_card_number}${request.ghana_card_matches ? ' ✓ matches verified card' : ''}`} />
        <Row label="Prescriber" value={`${request.prescriber_name} · ${request.prescriber_licence}`} />
        <Row label="Prescription date" value={request.prescription_date} />
        <Row label="Medicines" value={(request.product_names || []).join(', ')} />
      </div>
      {canApprove && <AuthImage src={`/controlled/requests/${request.request_id}/prescription`} alt="Prescription" className="h-40 w-full rounded border" />}
      {canApprove ? (
        <div className="space-y-2">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Note (required to reject)" data-testid="ctrl-decision-reason" />
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => decide('rejected')} disabled={busy} data-testid="ctrl-reject-btn"><XCircle className="h-4 w-4 mr-1" />Reject</Button>
            <Button className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => decide('approved')} disabled={busy || !request.identity?.verified} data-testid="ctrl-approve-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}Approve sale
            </Button>
          </div>
          {!request.identity?.verified && <p className="text-[11px] text-amber-700">Verify the Ghana Card above before approving.</p>}
        </div>
      ) : (
        <p className="flex items-center gap-2 text-sm text-amber-700" data-testid="ctrl-waiting"><Hourglass className="h-4 w-4 animate-pulse" />Waiting for a pharmacist to approve. They can approve from Controlled Registry.</p>
      )}
    </div>
  );
};

export const usePollRequest = (request, setRequest) => {
  useEffect(() => {
    if (!request || request.status !== 'pending') return undefined;
    const t = setInterval(() => {
      api.get(`/controlled/requests/${request.request_id}`).then(r => setRequest(r.data)).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [request, setRequest]);
};
